import axios from 'axios'

/**
 * @namespace UserService
 * @description Service for handling user-related API operations in the provider section
 */
const UserService = {

  /**
   * Get users with pagination and filtering
   * @param {Object} params - Query parameters
   * @param {Number} params.page - Current page
   * @param {Number} params.per_page - Items per page
   * @param {String} params.sort_by - Sort field
   * @param {Boolean} params.sort_desc - Sort direction
   * @param {String} params.filter_text - Search term
   * @returns {Promise} Promise object with users data
   */
  async get (params = {}) {
    return axios.get('provider/users', { params })
  },

  /**
   * Creates a new user
   * @param {Object} [payload={}] - User data to be created
   * @param {string|number} payload.role_id - Role ID of the user
   * @param {string|number} payload.organization_id - Organization ID of the user
   * @param {string} payload.username - Username of the user
   * @param {string} payload.first_name - First name of the user
   * @param {string} payload.last_name - Last name of the user
   * @param {string} payload.email - Email address of the user
   * @param {string} payload.password - Password for the user
   * @param {string} payload.password_confirmation - Password confirmation for the user
   * @param {number} payload.login_local - Local login flag (0 or 1)
   * @param {number} payload.active - Active status of the user (0 or 1)
   * @returns {Promise<Any>} Promise resolving to the created user data
   */
  async post (payload = {}) {
    return axios.post('provider/users', payload)
  },

  /**
   * Updates an existing user
   * @param {Object} [payload={ id: '' }] - User data to be created
   * @param {string|number} payload.id - ID of the user to update
   * @param {string|number} payload.role_id - Role ID of the user
   * @param {string|number} payload.organization_id - Organization ID of the user
   * @param {string} payload.username - Username of the user
   * @param {string} payload.first_name - First name of the user
   * @param {string} payload.last_name - Last name of the user
   * @param {string} payload.email - Email address of the user
   * @param {string} payload.password - Password for the user
   * @param {string} payload.password_confirmation - Password confirmation for the user
   * @param {number} payload.login_local - Local login flag (0 or 1)
   * @param {number} payload.active - Active status of the user (0 or 1)
   * @returns {Promise<Any>} Promise resolving to the created user data
   */
  async put (payload = { id: '' }) {
    return axios.put(`provider/users/${payload.id}`, payload)
  },

  /**
   * Deletes a user
   * @param {Object} [data={ id: '' }] - User data for deletion
   * @param {string|number} data.id - ID of the user to delete
   * @returns {Promise<Any>} Promise resolving to the deletion response
   */
  async delete (data = { id: '' }) {
    return axios.delete(`provider/users/${data.id}`, { data })
  }
}

export default UserService
